import { authenticatedRequest, backendRequest, fitsKeepalive, keepaliveRequest } from '@/services';
import { ApiHost } from '@/lib/constants';
import { HttpMethod } from '@/lib/enums';
import { deserializeDraft, serializeDraftPatch } from '../lib/draft-serde';
import type { AudienceDraft, DraftPatch, DraftResponse, WireDraft } from '../types/draft';

const RESOURCE = 'audience/requests';

export async function createDraft(patch: DraftPatch, campaignId?: string): Promise<AudienceDraft> {
  const serialized = serializeDraftPatch(patch) as unknown as Record<string, unknown>;
  if (campaignId) {
    serialized.campaignId = campaignId;
  }
  const data = await authenticatedRequest({
    method: HttpMethod.POST,
    resource: RESOURCE,
    data: serialized,
    skipRefreshInterceptor: true,
  });
  return deserializeDraft(data as WireDraft);
}

export async function getDraft(id: string): Promise<AudienceDraft> {
  const data = await authenticatedRequest({
    method: HttpMethod.GET,
    resource: `${RESOURCE}/${id}`,
  });
  return deserializeDraft(data as WireDraft);
}

type RawHttpError = { status?: number; data?: Record<string, unknown> };
type WireErrorBody = {
  code?: string;
  message?: string;
  context?: { current?: WireDraft; errors?: Array<{ field: string; message: string }> };
  current?: WireDraft;
};

/**
 * A failed draft save, shaped for useAutosave: the HTTP status plus the backend's
 * `{ error: { code, message, context } }` body. The stale-draft 409 carries the server
 * draft as `context.current` (or top-level `current`), which feeds the ConflictModal.
 */
export class DraftSaveError extends Error {
  readonly status?: number;
  readonly body: {
    code?: string;
    message?: string;
    fieldErrors?: Record<string, string>;
    current?: WireDraft;
  };

  constructor(error: unknown) {
    const { status, data } = (error ?? {}) as RawHttpError;
    const apiError = (data?.error ?? {}) as WireErrorBody;
    super(apiError.message ?? "Couldn't save the draft. Please try again.");
    this.name = 'DraftSaveError';
    this.status = status;
    this.body = {
      code: apiError.code,
      message: apiError.message,
      fieldErrors: apiError.context?.errors
        ? Object.fromEntries(apiError.context.errors.map((e) => [e.field, e.message]))
        : undefined,
      current: apiError.context?.current ?? apiError.current ?? (data?.current as WireDraft),
    };
  }
}

/**
 * Autosave PATCH. Calls `backendRequest` directly, not `authenticatedRequest`: the latter
 * rethrows the bare `{ code, message }` and drops the HTTP status, so a 409 would look
 * like a network blip and never open the ConflictModal (B15).
 *
 * `lastSeenUpdatedAt` is sent only when we hold one; the backend skips the stale check
 * without it.
 *
 * `keepalive` (NPJB-839 #103) is for a save fired while the tab closes: the browser
 * cancels a normal request then, but still sends a keepalive one. A body over the
 * keepalive cap falls back to the normal request, which may then be lost.
 */
export async function patchDraft(
  id: string,
  patch: DraftPatch,
  lastSeenUpdatedAt: string | null,
  { keepalive = false }: { keepalive?: boolean } = {},
): Promise<DraftResponse> {
  const body = {
    ...serializeDraftPatch(patch),
    ...(lastSeenUpdatedAt ? { lastSeenUpdatedAt } : {}),
  };
  const request = {
    method: HttpMethod.PATCH,
    apiHost: ApiHost.BackendService,
    apiVersion: '/api',
    resource: `${RESOURCE}/${id}`,
    data: body,
    skipRefreshInterceptor: true,
  };
  const send = keepalive && fitsKeepalive(JSON.stringify(body)) ? keepaliveRequest : backendRequest;
  try {
    const response = await send(request);
    return response.data as DraftResponse;
  } catch (error: unknown) {
    throw new DraftSaveError(error);
  }
}

export type SubmitDraftBody = {
  name?: string;
};

export async function submitDraft(
  id: string,
  lastSeenUpdatedAt: string | null,
  body?: SubmitDraftBody,
): Promise<AudienceDraft> {
  const data = await authenticatedRequest({
    method: HttpMethod.POST,
    resource: `${RESOURCE}/${id}/submit`,
    data:
      body?.name !== undefined && body.name !== ''
        ? { lastSeenUpdatedAt, name: body.name }
        : { lastSeenUpdatedAt },
  });
  return deserializeDraft(data as WireDraft);
}
